import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useAccelContext } from "../../contexts/AccelContext/AccelContext";
import { mix } from "polished";

interface Category {
  total: number;
  counterparties: {
    [key: string]: number;
  };
}

interface Subsection {
  title: string;
  value: number;
  categories: {
    [key: string]: Category;
  };
}

interface Section {
  title: string;
  value: number;
  subsections: Subsection[];
}

interface IncomeStatementWidgetProps {
  title: string;
  netIncome: number;
  sections: Section[];
}

const IncomeStatementWidget: React.FC<IncomeStatementWidgetProps> = ({
  title,
  netIncome,
  sections,
}) => {
  const { theme } = useAccelContext();
  const halfwayColor = mix(
    0.2,
    theme?.secondary?.hex || "#000000",
    theme?.accent?.hex || "#FFFFFF"
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: theme?.accent?.hex,
        color: theme?.text?.hex,
      }}
    >
      <Accordion
        defaultExpanded
        sx={{
          width: "100%",
          boxShadow: "none",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore sx={{ color: theme?.text?.hex }} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            backgroundColor: halfwayColor,
            marginLeft: "16px",
            marginRight: "16px",
          }}
        >
          <Typography sx={{ flexShrink: 0, fontWeight: "bold" }}>
            {title}
          </Typography>
          <Typography sx={{ marginLeft: "auto" }}>
            {netIncome}
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            marginBottom: "16px",
            width: "100%",
          }}
        >
          {sections.map((section, index) => (
            <Accordion
              key={index}
              defaultExpanded
              sx={{
                color: theme?.text?.hex || "#000000",
                marginBottom: "16px",
                marginTop: "16px",
                boxShadow: "none",
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMore sx={{ color: theme?.text?.hex || "#000000" }} />
                }
                aria-controls={`panel${index + 2}a-content`}
                id={`panel${index + 2}a-header`}
                sx={{
                  backgroundColor: theme?.accent?.hex,
                  marginLeft: "16px",
                  marginRight: "24px",
              
                }}
              >
                <Typography sx={{ flexShrink: 0, fontWeight: "bold" }}>
                  {section?.title}
                </Typography>
                <Typography sx={{ marginLeft: "auto" }}>
                  {section?.value}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: "16px" }}>
                {section?.subsections?.map((subsection, subIndex) => (
                  <Accordion
                    key={subIndex}
                    sx={{ marginBottom: "8px", boxShadow: "none" }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMore
                          sx={{
                            color: theme?.text?.hex || "#000000",
                            backgroundColor: theme?.accent?.hex,
                          }}
                        />
                      }
                    >
                      <Typography sx={{ flexShrink: 0, fontWeight: "bold" }}>
                        {subsection?.title}
                      </Typography>
                      <Typography sx={{ marginLeft: "auto" }}>
                        {subsection?.value}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {Object.entries(subsection?.categories || {}).map(
                        ([categoryName, category], catIndex) => (
                          <Accordion
                            key={catIndex}
                            sx={{ marginBottom: "8px", boxShadow: "none" }}
                          >
                            <AccordionSummary
                              expandIcon={
                                <ExpandMore
                                  sx={{
                                    color: theme?.text?.hex || "#000000",
                                    backgroundColor: theme?.accent?.hex,
                                  }}
                                />
                              }
                            >
                              <Typography
                                sx={{ flexShrink: 0, fontWeight: "bold" }}
                              >
                                {categoryName}
                              </Typography>
                              <Typography sx={{ marginLeft: "auto" }}>
                                {category.total}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {Object.entries(category?.counterparties || {}).map(
                                ([counterparty, amount], cpIndex) => (
                                  <Box
                                    key={cpIndex}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      padding: "4px 0",
                                    }}
                                  >
                                    <Typography>{counterparty}</Typography>
                                    <Typography>{amount}</Typography>
                                  </Box>
                                )
                              )}
                            </AccordionDetails>
                          </Accordion>
                        )
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default IncomeStatementWidget;
